import {
  applySpec,
  includes,
  length,
  filter,
  toPairs,
  pipe,
  path,
  pick,
  all,
  map,
  prop,
  pathOr
} from 'ramda'
import { useCallback, useMemo } from 'react'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from '@/hooks/redux'
import useProfile from '@/hooks/use-profile'
import useContent from '@/components/onboarding-toaster/content'
import {
  Task,
  Step,
  Toaster,
  StepData,
  TaskData,
  TaskState,
  HighlightType,
  TaskKey,
  BadgeState,
  TourItems
} from '@/components/onboarding-toaster/store'
import { State } from '@/components/onboarding-toaster/types'
import { headObj } from '@/utils'
import { flags } from '@/flags'

export const useToaster = () => {
  const dispatch = useDispatch()
  const toaster = dispatch.toaster
  const data = useSelector<Toaster | undefined>(path(['toaster']))
  const values = useMemo(() => ({ open: !data?.stowaway, ...data, ...toaster }), [data, toaster])
  return values
}

export const useHighlight = (
  key: string,
  variant: HighlightType = true,
  next: HighlightType = false
) => {
  const [task, step] = key.split('.')
  const dispatch = useDispatch()
  const toaster = dispatch.toaster

  const selectHighlightData = createSelector(
    [
      pathOr('', ['toaster', 'tasks', task, 'steps', step, 'data', 'highlight']),
      pathOr(false, ['toaster', 'enabled'])
    ],
    (highlight, isToasterEnabled) => ({
      hide:
        highlight !== variant ||
        !isToasterEnabled ||
        !pathOr(false, ['features', 'toaster'], flags),
      next: () => {
        if (!pathOr(false, ['features', 'toaster'], flags)) return
        toaster.updateState(key, { data: { highlight: next }, stowaway: true })
      },
      skip: () => {
        if (!pathOr(false, ['features', 'toaster'], flags)) return
        toaster.updateState(key, { data: { highlight: false }, stowaway: true })
      }
    })
  )
  return useSelector(selectHighlightData)
}

export const useTaskStates = () => {
  const { getState } = useComputeTaskState()
  const states = useSelector<Partial<Record<TaskKey, TaskState>>>(state => {
    const values = Object.entries(state?.toaster?.tasks || {})
    return values.reduce((acc, next) => {
      const key = next[0]
      const task = next[1]
      const value = getState(key, task)
      const obj = next ? { [key]: value } : {}
      return { ...acc, ...obj }
    }, {})
  })
  return states
}

export const useTask = (task: string) => {
  const { getState } = useComputeTaskState()
  const select = ['toaster', 'tasks', task]
  const data = useSelector<Task | undefined>(path(select))
  const state = getState(task, data)
  const { steps, ...rest } = data || {}
  return { ...rest, state }
}

const useLocked = (key: string, state: State) => {
  const validData = useSelector(pick(['profile', 'toaster']))
  return getLocked(key, state, validData)
}

// This method computes if the state should be locked
// due to invalid valid_tour_items or not.
// Otherwise we return the saved state.
// (Nasty method but BE should really be doing this)
const getLocked = (key: string, state: State, validData: any): State => {
  const func = (() => {
    switch (key) {
      case 'application.invite':
        return pipe(
          path(['toaster', 'valid_tour_items', 'timeline_no_application']),
          (value: unknown): State => (value || state !== 'incomplete' ? state : 'locked')
        )
      case 'application.received':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_with_locked_application']),
          (value: unknown): State => {
            if (value) return 'complete'
            if (state === 'complete') return state
            return 'incomplete'
          }
        )
      case 'application.trust_score':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
          (value: unknown): State => {
            if (!value) return 'locked'
            if (state === 'complete') return state
            return 'incomplete'
          }
        )
      case 'complete_contract.create_contract':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
          (value: unknown): State => {
            if (state === 'complete') return state
            if (!value) return 'locked'
            return state
          }
        )
      case 'complete_contract.add_addendum':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
          (value: unknown): State => {
            if (state === 'complete') return state
            if (!value) return 'locked'
            return 'incomplete'
          }
        )
      case 'complete_contract.send_contract':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
          (value: unknown): State => {
            if (state === 'complete') return state
            if (!value) return 'locked'
            return 'incomplete'
          }
        )
      case 'complete_contract.sign_contract':
        return pipe(
          applySpec({
            value: path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
            contract: pipe(path(['toaster', 'support_items_data', 'contracts']), headObj)
          }),
          ({ contract, value }: { contract: { state_machine: string }; value: unknown }): State => {
            const contractState = contract?.state_machine
            if (contractState === 'signed' || contractState === 'active') return 'complete'
            if (contractState === 'sent') return 'locked'
            if (state === 'complete') return state
            if (!value) return 'locked'
            return 'incomplete'
          }
        )
      case 'find_tenants.find_tenants':
        return pipe(
          path(['toaster', 'valid_tour_items', 'listing_published']),
          (value: unknown): State => {
            if (state === 'complete') return state
            if (!value) return 'locked'
            return 'incomplete'
          }
        )
      case 'chat_leads.view':
        return pipe(
          path(['toaster', 'valid_tour_items', 'timeline_no_application']),
          (value: unknown): State => {
            if (!value) return 'locked'
            if (state === 'complete') return state
            return 'incomplete'
          }
        )
      case 'chat_leads.create':
        return pipe(
          path(['toaster', 'valid_tour_items', 'timeline_no_application']),
          (value: unknown): State => {
            if (!value) return 'locked'
            if (state === 'complete') return state
            return 'incomplete'
          }
        )
      case 'verify_profile.license':
        return pipe(
          path(['profile', 'company_info', 'business_licence_verified_state']),
          (value: unknown): State => {
            if (value === 'pending' || value === 'verified') return 'complete'
            return state
          }
        )
      case 'verify_profile.email':
        return pipe(path(['profile', 'email_verified_state']), (value: unknown): State => {
          if (value === 'passed') return 'complete'
          return state
        })
      case 'verify_profile.id':
        return pipe(path(['profile', 'identity_verified_state']), (value: unknown): State => {
          if (!value || value === 'declined') return state
          return 'complete'
        })
      default:
        return () => state
    }
  })()
  const data = func(validData)
  return data ?? 'incomplete'
}

const useBadge = (key: string): BadgeState => {
  const data = useSelector<BadgeState | undefined>(
    (() => {
      switch (key) {
        case 'verify_profile.license':
          return pipe(
            path(['session', 'session', 'company_info', 'business_licence_verified_state']),
            (state: string | undefined): BadgeState => (state === 'pending' ? 'pending' : 'none')
          )
        case 'verify_profile.id':
          return pipe(
            path(['session', 'session', 'identity_verified_state']),
            (state: string | undefined): BadgeState => (state === 'pending' ? 'pending' : 'none')
          )
        case 'publish_listing.verify':
          return (): BadgeState => 'none'
        case 'complete_contract.sign_contract':
          return pipe(
            applySpec({
              value: path(['toaster', 'valid_tour_items', 'listing_yes_application_no_contract']),
              contract: pipe(path(['toaster', 'support_items_data', 'contracts']), headObj)
            }),
            ({
              contract,
              value
            }: {
              contract: { state_machine: string }
              value: unknown
            }): BadgeState => {
              const contractState = contract?.state_machine
              if (!contractState) return 'none'
              if (contractState === 'sent') return 'pending'
              return 'none'
            }
          )
        default:
          return (): BadgeState => 'none'
      }
    })()
  )
  return data ?? 'none'
}

export const useState = (key: string) => {
  const { shouldShow } = useShouldShow()
  const [task, step] = key.split('.')
  const topData = ['toaster', 'tasks', task, 'data']
  const select = ['toaster', 'tasks', task, 'steps', step]
  const {
    step: stepData,
    data,
    tourItems
  } = useSelector<{ step: Step | undefined; data: TaskData; tourItems: TourItems }>(
    applySpec({
      step: path(select),
      data: path(topData),
      tourItems: path(['toaster', 'valid_tour_items'])
    })
  )
  const original = stepData?.state
  const show = shouldShow(key, stepData?.data?.show_for)
  const state = useLocked(key, original ?? 'incomplete')
  const adjustedState = !show ? 'complete' : state
  const badge = useBadge(key)
  return { state: adjustedState, badge, data, tourItems }
}

export const useIncompleteCount = () => {
  const data = useContent()
  const visibleTasks = map(prop('key'), data.tasks)
  const { getState } = useComputeTaskState()
  const count: number = useSelector(
    pipe(
      path(['toaster', 'tasks']),
      data => (data || {}) as object,
      pick(visibleTasks),
      toPairs,
      filter(([key, state]) => getState(key, state) !== 'complete'),
      length
    )
  )
  return { count, data }
}

const useShouldShow = () => {
  const profile = useProfile()
  const validTourItems = useSelector(path(['toaster', 'valid_tour_items']))
  const data = useMemo(
    () => ({ profile, toaster: { valid_tour_items: validTourItems } }),
    [validTourItems, profile]
  )
  const role = profile?.account_type
  const preApprove = profile?.company_info?.listings_pre_approved
  const listingPreVerified = preApprove === '1' || preApprove === 1
  const shouldShow = useCallback(
    (key: string, showFor: string[] | undefined) =>
      (!showFor || includes(role, showFor)) &&
      (key !== 'publish_listing.verify' || !listingPreVerified),
    [role, listingPreVerified]
  )
  const getAugmentedState = useCallback(
    (key: string, step: State) => getLocked(key, step, data),
    [data]
  )
  return { role, shouldShow, getAugmentedState }
}

const useComputeTaskState = () => {
  const { role, shouldShow, getAugmentedState } = useShouldShow()
  const getState = useCallback(
    (taskKey: string, task: Task | undefined): TaskState => {
      const steps = Object.entries(task?.steps || {})
      if (steps.length <= 0) return 'incomplete'
      const isComplete = steps.every(
        ([key, step]) =>
          !shouldShow(`${taskKey}.${key}`, step?.data?.show_for) ||
          getAugmentedState(`${taskKey}.${key}`, step?.state!) === 'complete'
      )
      return isComplete ? 'complete' : 'incomplete'
    },
    [shouldShow, getAugmentedState]
  )
  return { role, shouldShow, getState }
}

export const useCheckTasksCompleted = (tasks: TaskKey[]): boolean => {
  const states = useTaskStates()
  const selectedTasks = pick(tasks, states)
  const taskStates = Object.values(selectedTasks || {})
  const completed = all(state => state === 'complete')(taskStates)
  return completed
}
